import axios from 'axios';
import { useState, useCallback, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

const usePlaid = ({
  name,
  onExit = () => null,
  onSuccess = () => null,
  callback = () => null,
  onError = () => null,
  onEvent = () => null,
  plaidLinkTokenEndPoint,
  setAppLoading,
} = {}) => {
  const [plaidRef, setPlaidRef] = useState(null);
  const { isPlaidEnabled, ...plaidConfig } = useSelector(({ data, groups, ui }) => {
    if (!name) {
      return { isPlaidEnabled: true };
    }
    const group = groups[name];
    const { configs } = group;
    const shouldPrompt =
      name === 'FUNDING_TRANSFER_EXT' || ui.oauth_state_id
        ? !group.fields?.includes('appRequest_externalPaymentAccountNumber')
        : true; // for the home page

    return {
      clientName: configs.clientName,
      countryCodes: configs.countryCodes,
      env: configs.environment,
      isPlaidEnabled:
        configs.isPlaidEnabled &&
        (ui.activeGroup === name || ui.oauth_state_id) &&
        data.appRequest_fundingMethod === 'AUTOMATIC_DEBIT' &&
        shouldPrompt,
      oauthStateId: ui.oauth_state_id,
      product: configs.products,
      webhook: configs.webhookUrl,
    };
  }, shallowEqual);

  const runPlaid = useCallback(
    (plaidConfigProp = {}) => {
      if (isPlaidEnabled) {
        if (!plaidRef) {
          const script = document.createElement('script');
          script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
          script.async = true;
          script.id = 'plaid';
          const plaidLinkToken = localStorage.getItem('plaidLinkToken');
          if (!plaidLinkToken) {
            setAppLoading(true);
            axios
              .get(plaidLinkTokenEndPoint)
              .then((res) => {
                const { data } = res;
                localStorage.setItem('plaidLinkToken', data.data.linkToken);
                script.onload = () => {
                  const plaid = window.Plaid.create({
                    ...plaidConfig,
                    ...plaidConfigProp,
                    onEvent,
                    onExit,
                    onSuccess,
                    token: data.data.linkToken,
                  });
                  setPlaidRef(plaid);
                  setAppLoading(false);
                };
                script.onerror = (err) => {
                  window.console.log('error', err);
                };
                document.body.appendChild(script);
              })
              .catch((err) => {
                onExit();
                setAppLoading(false);
                onError(err.response.data);
              });
          } else {
            script.onload = () => {
              const plaid = window.Plaid.create({
                ...plaidConfig,
                ...plaidConfigProp,
                onEvent,
                onExit,
                onSuccess,
                receivedRedirectUri: `${window.location.href}?oauth_state_id=${plaidConfig.oauthStateId}`,
                token: plaidLinkToken,
              });
              setPlaidRef(plaid);
              setAppLoading(false);
            };
            script.onerror = (err) => {
              window.console.log('error', err);
              onExit();
              setAppLoading(false);
              onError(err.response.data);
            };
            document.body.appendChild(script);
          }
        }
        callback();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPlaidEnabled, plaidConfig.key, plaidRef, name],
  );

  useEffect(
    () => () => {
      if (plaidRef) {
        plaidRef.destroy();
      }
    },
    [plaidRef],
  );

  return [plaidRef, runPlaid, isPlaidEnabled];
};

export default usePlaid;
